import type { FeatureInfoType, Line, Polygon } from '@aus-platform/cesium';
import { useAppSelector } from 'app/hooks';
import { isEmpty, omit } from 'lodash';
import { selectMap3dWorkspace } from 'map-3d/shared/map-3d-slices';
import { SelectToolEventType } from '../action-tools';
import type { WorkspaceTools } from '../enums';
import { useEffect, useState } from 'react';
import { type EditingLock } from './constants';
import { deriveEditingLock } from './helpers';
import { LineEventType } from './line-tool';
import { PolygonEventType } from './polygon-tool';
import type { FeatureLockState } from './types';

/*
 * Observes how much of the current selection can actually be edited at current zoom.
 */
export const useEditingLockState = (
  activeTool: WorkspaceTools | null,
): EditingLock => {
  // States.
  const [lockStates, setLockStates] = useState<
    Record<string, FeatureLockState>
  >({});

  // Selectors.
  const { drawingTools, actionTools } = useAppSelector(selectMap3dWorkspace);

  // useEffects.
  useEffect(() => {
    const polygonTool = drawingTools?.polygonTool;
    const lineTool = drawingTools?.lineTool;

    if (!polygonTool && !lineTool) {
      return;
    }

    const handleEditingLockChanged = (
      feature: Polygon | Line,
      locked: boolean,
    ) => {
      setLockStates((currentStates) => ({
        ...currentStates,
        [feature.id]: { locked, handlesVisible: feature.handlesVisible },
      }));
    };

    polygonTool?.addEventListener(
      PolygonEventType.EditingLockChanged,
      handleEditingLockChanged,
    );
    lineTool?.addEventListener(
      LineEventType.EditingLockChanged,
      handleEditingLockChanged,
    );

    return () => {
      polygonTool?.removeEventListener(
        PolygonEventType.EditingLockChanged,
        handleEditingLockChanged,
      );
      lineTool?.removeEventListener(
        LineEventType.EditingLockChanged,
        handleEditingLockChanged,
      );
    };
  }, [drawingTools]);

  useEffect(() => {
    const selectFeatureTool = actionTools?.selectFeatureTool;

    if (!selectFeatureTool) {
      return;
    }

    const handleFeatureDeselected = (featureInfoArray: FeatureInfoType[]) => {
      const deselectedIds = featureInfoArray?.map(({ id }) => id);

      if (isEmpty(deselectedIds)) {
        return;
      }

      setLockStates((currentStates) => omit(currentStates, deselectedIds));
    };

    selectFeatureTool.addEventListener(
      SelectToolEventType.FeatureDeselected,
      handleFeatureDeselected,
    );

    return () => {
      selectFeatureTool.removeEventListener(
        SelectToolEventType.FeatureDeselected,
        handleFeatureDeselected,
      );
    };
  }, [actionTools]);

  useEffect(() => {
    setLockStates({});
  }, [activeTool]);

  return deriveEditingLock(
    lockStates,
    actionTools?.selectFeatureTool.selectedFeatures.map(({ id }) => id) ?? [],
  );
};
